import { push } from "@lagunovsky/redux-react-router";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { Toaster } from "../../components/Toast";
import httpClient from "../../config/http-client";

import {
  AUTH0_CREATE_SESSION,
  AUTH0_EXCHANGE_CODE,
  Auth0CreateSessionFailure,
  Auth0CreateSessionRequested,
  Auth0CreateSessionSuccess,
  Auth0ExchangeCodeFailure,
  Auth0ExchangeCodeRequested,
  Auth0ExchangeCodeSuccess,
  SIGNOUT,
  signoutSuccess,
  updateSession,
  UpdateUserFailure,
  UpdateUserPasswordFailure,
  UpdateUserPasswordRequested,
  UpdateUserPasswordSuccess,
  UpdateUserRequested,
  UpdateUserSuccess,
  UPDATE_USER,
  UPDATE_USER_PASSWORD,
} from "../actions/session-action-types";

export function* signout() {
  yield put(signoutSuccess());
  yield put(push("/signout"));
}

export function* UpdateUser({ payload: data }) {
  yield put(UpdateUserRequested());

  const appUserId = yield select(
    ({
      session: {
        user: { id },
      },
    }) => id
  );

  const { result, error } = yield call(httpClient, {
    data,
    method: "put",
    url: `/manage/users/${appUserId}`,
  });

  if (error) {
    yield put(UpdateUserFailure(error));
    Toaster({ message: "User update failed.", type: "error" });
  } else {
    yield put(UpdateUserSuccess(result.payload.user));
    Toaster({ message: "User successfully updated." });
  }

  return { error, result };
}

export function* UpdateUserPassword({ payload: { data, appUserId, callback } }) {
  yield put(UpdateUserPasswordRequested());

  const { result, error } = yield call(httpClient, {
    data,
    method: "put",
    url: `/manage/users/${appUserId}/password`,
  });

  if (error) {
    yield put(UpdateUserPasswordFailure(error));
    Toaster({ message: "User password update failed.", type: "error" });
  } else {
    yield put(UpdateUserPasswordSuccess(result.payload));
    Toaster({ message: "User password successfully updated." });
  }

  if (callback) callback();

  return { error, result };
}

export function* Auth0ExchangeCode({ payload: { code, redirectUri } }) {
  yield put(Auth0ExchangeCodeRequested());

  if (!code) {
    yield put(Auth0ExchangeCodeFailure("Missing authorization code."));
    return;
  }

  if (!redirectUri) {
    yield put(Auth0ExchangeCodeFailure("Missing Auth0 callback URL configuration."));
    return;
  }

  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  if (!clientId) {
    yield put(Auth0ExchangeCodeFailure("Missing Auth0 client ID configuration."));
    return;
  }

  const { result, error } = yield call(httpClient, {
    data: {
      code,
      redirectUri,
      clientId,
    },
    method: "post",
    url: "/manage/auth0/exchange",
  });

  if (error) {
    const message = error?.response?.data?.error?.details?.[0]?.message || "Failed to exchange code for accounts.";
    yield put(Auth0ExchangeCodeFailure(message));
    return;
  }

  const accounts = Array.isArray(result?.payload?.accounts) ? result.payload.accounts : [];
  const userId = result?.payload?.user?.id || "";
  const idToken = result?.payload?.idToken || "";

  if (!accounts.length) {
    yield put(Auth0ExchangeCodeFailure("No accounts found for this user."));
    return;
  }

  if (accounts.length === 1) {
    const accountId = accounts[0]?.id || "";
    if (!accountId) {
      yield put(Auth0ExchangeCodeFailure("No valid account id was returned."));
      return;
    }

    if (!userId || !idToken) {
      yield put(Auth0ExchangeCodeFailure("Exchange response is missing userId or idToken."));
      return;
    }

    yield call(Auth0CreateSession, {
      payload: {
        accountId,
        userId,
        idToken,
      },
    });
    return;
  }

  if (!userId || !idToken) {
    yield put(Auth0ExchangeCodeFailure("Exchange response is missing userId or idToken."));
    return;
  }

  yield put(
    Auth0ExchangeCodeSuccess({
      accounts,
      userId,
      idToken,
    })
  );
}

export function* Auth0CreateSession({ payload: { accountId, userId: payloadUserId, idToken: payloadIdToken } }) {
  yield put(Auth0CreateSessionRequested(accountId));

  if (!accountId) {
    yield put(Auth0CreateSessionFailure("No account selected."));
    return;
  }

  const storedUserId = yield select(({ session }) => session?.auth0UserId);
  const storedIdToken = yield select(({ session }) => session?.auth0IdToken);
  const userId = payloadUserId || storedUserId;
  const idToken = payloadIdToken || storedIdToken;

  if (!userId || !idToken) {
    yield put(Auth0CreateSessionFailure("Missing userId or idToken for session creation."));
    return;
  }

  const { result, error } = yield call(httpClient, {
    data: {
      accountId,
      userId,
    },
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    method: "post",
    url: "/manage/sessions",
  });

  if (error) {
    const message = error?.response?.data?.error?.details?.[0]?.message || "Failed to create session.";
    yield put(Auth0CreateSessionFailure(message));
    return;
  }

  const session = result?.payload?.session || null;

  if (!session) {
    yield put(Auth0CreateSessionFailure("Session response did not include session data."));
    return;
  }

  yield put(updateSession(session));
  yield put(Auth0CreateSessionSuccess());
  yield put(push("/projects"));
}

function* Session() {
  yield all([
    takeLatest(SIGNOUT, signout),
    takeLatest(AUTH0_EXCHANGE_CODE, Auth0ExchangeCode),
    takeLatest(AUTH0_CREATE_SESSION, Auth0CreateSession),
    takeLatest(UPDATE_USER, UpdateUser),
    takeLatest(UPDATE_USER_PASSWORD, UpdateUserPassword),
  ]);
}

export default Session;
